import { useEffect, useMemo } from 'react';
import {
  GALLERY_SRC_PREFIX,
  galleryImageUrl,
  parseStockSrc,
  stillDoc,
  type TemplateDoc,
} from '@bannermatic/core';
import { Scene } from '@bannermatic/scene';

/**
 * Gallery pictures (`gallery:<id>`) are shown from inline SVG or the bundled photo until they
 * are uploaded.
 */
export const resolvePreviewSrc = (src: string) => {
  if (src.startsWith(GALLERY_SRC_PREFIX))
    return galleryImageUrl(src.slice(GALLERY_SRC_PREFIX.length)) ?? '';
  // Photo placeholders of gallery templates: the server redirects to the best Unsplash match
  const stock = parseStockSrc(src);
  if (stock) {
    const size = stock.orientation === 'portrait' ? 'w=600&h=900' : 'w=900';
    return `/api/stock/preview?q=${encodeURIComponent(stock.query)}&${size}${stock.orientation ? `&orientation=${stock.orientation}` : ''}`;
  }
  return src;
};

/** Built-in fonts for live previews outside the editor. */
export function useBuiltinFonts() {
  useEffect(() => {
    if (document.getElementById('bm-builtin-fonts')) return;
    const link = document.createElement('link');
    link.id = 'bm-builtin-fonts';
    link.rel = 'stylesheet';
    link.href = '/fonts/builtin.css';
    document.head.appendChild(link);
  }, []);
}

/** A template document rendered in the browser and scaled to fit a `box`×`box` square. */
export function DocPreview({
  doc: full,
  box,
  boxHeight = box,
}: {
  doc: TemplateDoc;
  /** Fits into box × boxHeight (a square by default). */
  box: number;
  boxHeight?: number;
}) {
  const doc = useMemo(() => stillDoc(full), [full]);
  const scale = Math.min(box / doc.width, boxHeight / doc.height);
  return (
    <div className="relative" style={{ width: doc.width * scale, height: doc.height * scale }}>
      <div
        className="absolute top-0 left-0"
        style={{
          width: doc.width,
          height: doc.height,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
        }}
      >
        <Scene doc={doc} mode="render" resolveSrc={resolvePreviewSrc} />
      </div>
    </div>
  );
}
